import { Group, SecurityClearance } from "@fairfleet/geotab";
import Stack from "@mui/material/Stack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { filterClearanceOptions, isProtectedClearanceId } from "./clearanceHelpers";
import { ClearanceEditor } from "./components/ClearanceEditor";
import { ClearanceList } from "./components/ClearanceList";
import { useClearanceMutations } from "./hooks/useClearanceMutations";
import { ROOT_SECURITY_GROUP_ID } from "@/constants/geotabGroupIds";
import { useGroups } from "@/hooks/groups/useGroups";
import { useSecurityClearances } from "@/hooks/groups/useSecurityClearances";
import { useDebounced } from "@/hooks/useDebounced";
import { getErrorMessage } from "@/utilities/error/getErrorMessage";
import { buildGroupIndex } from "@/utilities/groupIndex";
import { normalizeSearchText } from "@/utilities/text/normalizeSearchText";

const NO_GROUPS: Group[] = [];
const NO_SECURITY_CLEARANCES: SecurityClearance[] = [];
const NO_SECURITY_FILTERS: NonNullable<SecurityClearance["securityFilters"]> = [];

export default function ClearancesPage() {
  const { t } = useTranslation();
  const { data: groups = NO_GROUPS, isLoading, error: groupsError } = useGroups();
  const {
    data: securityBranchGroups = NO_SECURITY_CLEARANCES,
    isLoading: isLoadingSecurityBranch,
    error: securityBranchError,
  } = useSecurityClearances();
  const { save, remove } = useClearanceMutations();

  const [search, setSearch] = useState("");
  const [selectedClearanceId, setSelectedClearanceId] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const debouncedSearch = useDebounced(search, 250);

  const hasSecurityBranchData = securityBranchGroups.length > 0;

  const groupIndex = useMemo(
    () => buildGroupIndex<Group | SecurityClearance>([...groups, ...securityBranchGroups]),
    [groups, securityBranchGroups],
  );

  const clearanceOptions = useMemo(() => {
    const source = hasSecurityBranchData ? securityBranchGroups : groups;
    return filterClearanceOptions(source, groupIndex);
  }, [groups, hasSecurityBranchData, groupIndex, securityBranchGroups]);

  const filteredClearances = useMemo(() => {
    const query = normalizeSearchText(debouncedSearch);
    if (!query) return clearanceOptions;
    return clearanceOptions.filter(
      group =>
        normalizeSearchText(group.name).includes(query) ||
        normalizeSearchText(group.id).includes(query),
    );
  }, [clearanceOptions, debouncedSearch]);

  const selectedClearance = useMemo(
    () => clearanceOptions.find(group => group.id === selectedClearanceId) ?? null,
    [clearanceOptions, selectedClearanceId],
  );

  const parentOptions = useMemo(
    () =>
      clearanceOptions.filter(
        group => group.id === ROOT_SECURITY_GROUP_ID || !isProtectedClearanceId(group.id),
      ),
    [clearanceOptions],
  );

  const bothSourcesFailed = !!groupsError && !!securityBranchError;
  const actionError = save.error ?? remove.error;

  const resetMessages = () => {
    save.reset();
    remove.reset();
    setActionSuccess(null);
  };

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      spacing={2}
      sx={{ p: 2, height: "100%", boxSizing: "border-box", minWidth: 0 }}
    >
      <ClearanceList
        clearances={filteredClearances}
        selectedId={selectedClearanceId}
        search={search}
        onSearchChange={setSearch}
        onSelect={id => {
          resetMessages();
          setSelectedClearanceId(id);
        }}
        onNew={() => {
          resetMessages();
          setSelectedClearanceId(null);
        }}
        isLoading={isLoading || isLoadingSecurityBranch}
        hasLoadError={bothSourcesFailed || (!!groupsError && !hasSecurityBranchData)}
        showFallbackWarning={
          !isLoadingSecurityBranch &&
          !bothSourcesFailed &&
          (!!securityBranchError || !hasSecurityBranchData)
        }
      />

      <ClearanceEditor
        key={selectedClearance?.id ?? "new"}
        clearance={selectedClearance}
        parentId={
          selectedClearance?.parent?.id ??
          groupIndex.parentById.get(selectedClearance?.id ?? "") ??
          ROOT_SECURITY_GROUP_ID
        }
        parentOptions={parentOptions}
        securityFilters={(selectedClearance as SecurityClearance | null)?.securityFilters ?? NO_SECURITY_FILTERS}
        successMessage={actionSuccess}
        errorMessage={actionError ? getErrorMessage(actionError) : null}
        isSaving={save.isPending}
        isRemoving={remove.isPending}
        onSave={values => {
          resetMessages();
          save.mutate(
            {
              existing: selectedClearance ? { id: selectedClearance.id } : null,
              ...values,
            },
            {
              onSuccess: createdOrUpdatedId => {
                setActionSuccess(t("clearances.saveSuccess"));
                if (createdOrUpdatedId) setSelectedClearanceId(createdOrUpdatedId);
              },
            },
          );
        }}
        onRemove={() => {
          if (!selectedClearance) return;
          resetMessages();
          remove.mutate(selectedClearance.id, {
            onSuccess: () => {
              setActionSuccess(t("clearances.deleteSuccess"));
              setSelectedClearanceId(null);
            },
          });
        }}
      />
    </Stack>
  );
}
